import { api } from "@convex/_generated/api"; // Use alias
import { useAction } from "convex/react";
import {
	AlertCircle,
	CheckCircle2,
	CheckIcon,
	CopyIcon,
	Loader2,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import Toast from "@/components/_shared/Toast";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { usePageSettings } from "@/lib/cookies";

const defaultSettings = {
	defaultDetailLevel: 2,
	defaultTone: "technical",
	defaultTextLength: "short",
	darkMode: true,
};

interface InputSectionProps {
	inputText: string;
	setInputText: (text: string) => void;
	detailLevel: number;
	setDetailLevel: (level: number) => void;
	tone: string;
	setTone: (tone: string) => void;
	textLength: string;
	setTextLength: (textLength: string) => void;
	isGenerating: boolean;
	setIsGenerating: (isGenerating: boolean) => void;
	setOutputText: (text: string) => void;
	setError: (error: string | null) => void;
	onToast: (msg: { type: "success" | "error"; message: string }) => void;
}

export default function InputSection({
	inputText,
	setInputText,
	detailLevel,
	setDetailLevel,
	tone,
	setTone,
	textLength,
	setTextLength,
	isGenerating,
	setIsGenerating,
	setOutputText,
	setError,
	onToast,
}: InputSectionProps) {
	const { settings, updateSettings } = usePageSettings(
		"bulletpoints",
		defaultSettings,
	);
	const [copySuccess, setCopySuccess] = useState(false);
	const generateBulletpoints = useAction(api.bulletpoints.generateBulletpoints);

	const handleGenerate = async () => {
		if (!inputText.trim()) {
			setError("Bitte gib mindestens einen Hauptpunkt ein.");
			onToast({
				type: "error",
				message:
					"Bitte gib mindestens einen Hauptpunkt ein, der mit '-' beginnt.",
			});
			return;
		}

		// Check if input has at least one line starting with "-"
		const hasMainPoints = inputText
			.split("\n")
			.some((line) => line.trim().startsWith("-") && line.trim().length > 1);

		if (!hasMainPoints) {
			setError(
				"Bitte gib mindestens einen Hauptpunkt ein, der mit '-' beginnt.",
			);
			onToast({
				type: "error",
				message:
					"Bitte gib mindestens einen Hauptpunkt ein, der mit '-' beginnt.",
			});
			return;
		}

		setIsGenerating(true);
		setError(null);

		try {
			const data = await generateBulletpoints({
				input: inputText,
				detailLevel: Number(detailLevel),
				tone,
				textLength,
			});
			setOutputText(data.output);

			onToast({
				type: "success",
				message:
					"Generierung erfolgreich: Bulletpoints wurden erfolgreich erstellt.",
			});
		} catch (error) {
			console.error("Error generating bulletpoints:", error);
			setError(
				"Bei der Generierung ist ein Fehler aufgetreten. Bitte versuche es später erneut.",
			);

			onToast({
				type: "error",
				message:
					"Bei der Generierung ist ein Fehler aufgetreten. Bitte versuche es später erneut.",
			});
		} finally {
			setIsGenerating(false);
		}
	};

	// Funktion zum Kopieren des Eingabetexts
	const handleCopy = () => {
		navigator.clipboard.writeText(inputText);
		setCopySuccess(true);
		setTimeout(() => setCopySuccess(false), 2000);
		onToast({
			type: "success",
			message: "Kopiert! Eingabetext wurde in die Zwischenablage kopiert.",
		});
	};

	return (
		<>
			<p className="mb-4 text-gray-600 text-sm dark:text-gray-300">
				Gib die Hauptpunkte ein, die mit "-" beginnen. Der Assistent wird
				Unterpunkte generieren, die mit "--" beginnen.
			</p>

			<div className="relative mb-4">
				<Textarea
					className="input-area h-64 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
					onChange={(e) => setInputText(e.target.value)}
					placeholder={
						"- Installation neuer Hardware\n- Konfiguration des Netzwerks\n- Schulung des Personals"
					}
					value={inputText}
				/>
				{inputText && (
					<Button
						className="absolute right-2 bottom-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
						onClick={handleCopy}
						size="sm"
						type="button"
						variant="ghost"
					>
						{copySuccess ? <CheckIcon size={18} /> : <CopyIcon size={18} />}
					</Button>
				)}
			</div>

			<div className="mb-6 flex flex-wrap items-end gap-3">
				<div className="min-w-[120px] flex-1">
					<Label
						className="mb-1 text-gray-700 dark:text-gray-300"
						htmlFor="detailLevel"
					>
						Detailgrad
					</Label>
					<Select
						onValueChange={(value) => {
							setDetailLevel(Number(value));
							updateSettings({ defaultDetailLevel: Number(value) });
						}}
						value={detailLevel.toString()}
					>
						<SelectTrigger className="w-full border-gray-300 bg-white text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200">
							<SelectValue placeholder="Standard (3 Unterpunkte)" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="2">Minimal (2)</SelectItem>
							<SelectItem value="3">Standard (3)</SelectItem>
							<SelectItem value="5">Ausführlich (5)</SelectItem>
							<SelectItem value="7">Detailliert (7)</SelectItem>
						</SelectContent>
					</Select>
				</div>

				<div className="min-w-[120px] flex-1">
					<Label
						className="mb-1 text-gray-700 dark:text-gray-300"
						htmlFor="tone"
					>
						Schreibstil
					</Label>
					<Select
						onValueChange={(value) => {
							setTone(value);
							updateSettings({ defaultTone: value });
						}}
						value={tone}
					>
						<SelectTrigger className="w-full border-gray-300 bg-white text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200">
							<SelectValue placeholder="Technisch" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="technical">Technisch</SelectItem>
							<SelectItem value="formal">Formal</SelectItem>
							<SelectItem value="casual">Locker</SelectItem>
						</SelectContent>
					</Select>
				</div>

				<div className="min-w-[120px] flex-1">
					<Label
						className="mb-1 text-gray-700 dark:text-gray-300"
						htmlFor="textLength"
					>
						Textlänge
					</Label>
					<Select
						onValueChange={(value) => {
							setTextLength(value);
							updateSettings({ defaultTextLength: value });
						}}
						value={textLength}
					>
						<SelectTrigger className="w-full border-gray-300 bg-white text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200">
							<SelectValue placeholder="Normal" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="short">Kurz</SelectItem>
							<SelectItem value="normal">Normal</SelectItem>
							<SelectItem value="long">Lang</SelectItem>
						</SelectContent>
					</Select>
				</div>

				<Button
					className="bg-blue-600 text-white hover:bg-blue-700 md:min-w-[150px]"
					disabled={isGenerating || !inputText.trim()}
					onClick={handleGenerate}
					type="button"
				>
					{isGenerating ? (
						<>
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							Generiere...
						</>
					) : (
						"Bulletpoints generieren"
					)}
				</Button>
			</div>
		</>
	);
}
